import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { EMPTY, catchError } from 'rxjs';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { SupportApiService } from '../core/support-api.service';
import { SidekickFocusService } from '../core/sidekick-focus.service';
import { LiveRefreshService } from '../core/live-refresh.service';
import { SupportNotificationService } from '../core/support-notification.service';
import {
  TicketAccessLogEntry, TicketEventsPage, TicketFull, TicketMessageDetail, TicketSafe, TicketTransition,
} from '../core/models';
import { TicketThreadComponent } from './ticket-thread.component';
import { ComposeComponent } from './compose.component';
import { LivePanelComponent } from './live-panel.component';
import { EscalateModalComponent } from './escalate-modal.component';
import { AssignControlComponent } from './assign-control.component';
import { PriorityOverrideControlComponent } from './priority-override-control.component';
import { ResolveControlComponent } from './resolve-control.component';
import { priorityBadgeClass, slaBadgeClass, slaLabel, statusBadgeClass } from '../core/badge-maps';

@Component({
  selector: 'app-ticket-detail',
  standalone: true,
  imports: [
    CommonModule, RouterLink, NgbTooltip, TicketThreadComponent, ComposeComponent, LivePanelComponent,
    EscalateModalComponent, AssignControlComponent, PriorityOverrideControlComponent, ResolveControlComponent,
  ],
  template: `
    @if (projection === 'full' && full) {
      <div class="bt-page ticket-page">
        <a class="back-link" routerLink="/inbox" ngbTooltip="Back to the queue inbox">‹ Inbox</a>

        <div class="bt-card ticket-head" data-testid="ticket-header">
          <div class="ticket-head__main">
            <div class="ticket-head__badges">
              <!-- Ruling §2: reference is already "BT-<n>" from the API — render verbatim. -->
              <span class="ticket-head__ref" ngbTooltip="Ticket reference — assigned by the system, shown exactly as issued">{{ full.reference }}</span>
              <span class="bt-badge" [class]="statusClass(full.status)" data-testid="ticket-status" ngbTooltip="Current ticket status">{{ full.status }}</span>
              <span class="bt-badge" [class]="priorityClass(full.priority)" data-testid="ticket-priority" ngbTooltip="Priority — drives the SLA clock">
                {{ full.priority }}
                @if (full.priority_source === 'agent_override') {
                  <span class="override-dot" ngbTooltip="Overridden by an agent from the derived value">•</span>
                }
              </span>
              <span class="bt-badge" [class]="slaClass(full.first_response_sla_state)" data-testid="sla-countdown" [ngbTooltip]="slaTooltip()">
                {{ slaLabel(full.first_response_sla_state) }}
              </span>
            </div>
            <h1 class="ticket-head__subject">{{ full.subject }}</h1>
            <div class="ticket-head__meta">
              <span data-testid="requester" ngbTooltip="The address the requester used — replies go here">
                <span class="meta-k">Requester</span> {{ full.requester_email }}
              </span>
              <span ngbTooltip="When this ticket was opened"><span class="meta-k">Opened</span> {{ fmt(full.created_at) }}</span>
              <!-- Ruling §8: source_conversation_id may be null or point at a purged conversation. -->
              <span data-testid="source-conversation"
                    [ngbTooltip]="full.source_conversation_id
                      ? 'Opened from a live-chat conversation. It may since have been purged; this id is not verified to still resolve.'
                      : 'This ticket was not opened from a live-chat conversation.'">
                <span class="meta-k">Chat</span>
                @if (full.source_conversation_id) { {{ full.source_conversation_id }} } @else { No linked conversation }
              </span>
              @if (full.ado_work_item_id) {
                <a data-testid="ado-link" [href]="full.ado_work_item_url ?? '#'" target="_blank" rel="noopener"
                   ngbTooltip="Linked Azure DevOps work item">ADO&nbsp;#{{ full.ado_work_item_id }} ({{ full.ado_link_state }})</a>
              }
            </div>
          </div>
          <app-resolve-control [ticketId]="ticketId" [currentStatus]="full.status" (resolved)="onResolved()"></app-resolve-control>
          <button type="button" class="bt-btn bt-btn--secondary" data-testid="open-escalate"
                  (click)="showEscalateModal = !showEscalateModal"
                  ngbTooltip="Hand this ticket to the next tier with a required summary, steps tried, current state, and reason">
            Escalate
          </button>
        </div>
        @if (showEscalateModal) {
          <app-escalate-modal [ticketId]="ticketId" [currentStatus]="full.status"
                              [lastMessageSummary]="lastMessageSummary()" (escalated)="onEscalated()"></app-escalate-modal>
        }

        <div class="ticket-grid">
          <div class="ticket-main">
            <div class="bt-card conversation">
              <div class="card-head" ngbTooltip="The full message history — public replies the requester sees, plus internal notes only your team sees">
                Conversation
              </div>
              <div class="conversation__body">
                <app-ticket-thread [messages]="messages"></app-ticket-thread>
              </div>
              <div class="conversation__compose">
                <app-compose [ticketId]="ticketId" (messagePosted)="reloadThread()"></app-compose>
              </div>
            </div>

            <div class="bt-card">
              <!-- Ruling §14: Component 1 (live conversation panel) only. -->
              <app-live-panel [ticketId]="ticketId" [sourceConversationId]="full.source_conversation_id"></app-live-panel>
            </div>
          </div>

          <aside class="ticket-rail" data-testid="right-rail">
            <div class="bt-card rail-card" data-testid="assignment-controls">
              <div class="card-head" ngbTooltip="Assign this ticket to an agent and override its priority (a reason is required)">
                Assignment &amp; priority
              </div>
              <app-assign-control [ticketId]="ticketId" [assignableAgents]="assignableAgents"></app-assign-control>
              <app-priority-override-control [ticketId]="ticketId" [currentPriority]="full.priority"></app-priority-override-control>
            </div>

            <div class="bt-card rail-card" data-testid="transition-history">
              <div class="card-head" ngbTooltip="Every status, priority, and assignment change on this ticket, newest last">History</div>
              <ul class="history">
                @for (t of transitions; track t.id) {
                  <li [attr.data-testid]="'transition-' + t.kind" class="history__row">
                    @switch (t.kind) {
                      @case ('status') { <span class="history__k">Status</span> {{ t.from_status ?? '—' }} → {{ t.to_status }} <span class="history__who">{{ t.actor_type }}</span> }
                      @case ('priority') { <span class="history__k">Priority</span> {{ t.from_priority ?? '—' }} → {{ t.to_priority }} <span class="history__who">{{ t.actor_type }}</span> }
                      @case ('assignment') { <span class="history__k">Assigned</span> {{ t.from_assignee_id ?? 'unassigned' }} → {{ t.to_assignee_id ?? 'unassigned' }} <span class="history__who">{{ t.actor_type }}</span> }
                      @default { {{ t.kind }} <span class="history__who">{{ t.actor_type }}</span> }
                    }
                  </li>
                } @empty { <li class="history__empty">No changes yet</li> }
              </ul>
            </div>

            <div class="bt-card rail-card" data-testid="access-log-summary">
              <div class="card-head" ngbTooltip="CJIS read audit — who has opened this ticket's contents (non-requester principals)">Read audit</div>
              <p class="audit-line">
                <span class="audit-count">{{ accessLog.length }}</span> recorded read(s) by non-requester principals.
              </p>
            </div>
          </aside>
        </div>
      </div>
    } @else if (projection === 'safe' && safe) {
      <!-- VAR-safe projection branch (Ruling §14): TicketSafe fields only; no internal-note
           affordance, no live conversation panel, no assign control — genuinely absent from the DOM. -->
      <div class="bt-page ticket-page">
        <a class="back-link" routerLink="/inbox" ngbTooltip="Back to the queue inbox">‹ Inbox</a>
        <div class="bt-card ticket-head" data-testid="ticket-header">
          <div class="ticket-head__main">
            <div class="ticket-head__badges">
              <span class="ticket-head__ref">{{ safe.reference }}</span>
              <span class="bt-badge" [class]="statusClass(safe.status)" data-testid="ticket-status">{{ safe.status }}</span>
              <span class="bt-badge" [class]="priorityClass(safe.priority)" data-testid="ticket-priority">{{ safe.priority }}</span>
            </div>
          </div>
        </div>
        <div class="ticket-grid ticket-grid--safe">
          <div class="ticket-main">
            <div class="bt-card conversation">
              <div class="card-head">Conversation</div>
              <div class="conversation__body"><app-ticket-thread [messages]="messages"></app-ticket-thread></div>
              <div class="conversation__compose"><app-compose [ticketId]="ticketId" [replyOnly]="true" (messagePosted)="reloadThread()"></app-compose></div>
            </div>
            <app-escalate-modal [ticketId]="ticketId" [currentStatus]="safe.status" [lastMessageSummary]="lastMessageSummary()"></app-escalate-modal>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .ticket-page { max-width: 1180px; }
    .back-link { display: inline-block; margin-bottom: 0.75rem; color: var(--bt-text-secondary); text-decoration: none; font-size: 14px; font-weight: 600; }
    .back-link:hover { color: var(--bt-text-primary); }

    .ticket-head { display: flex; align-items: flex-start; gap: 1rem; padding: 1.1rem 1.25rem; margin-bottom: 1rem; }
    .ticket-head__main { flex: 1; min-width: 0; }
    .ticket-head__badges { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 0.5rem; }
    .ticket-head__ref { font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 15px; font-weight: 700; color: var(--bt-text-secondary); margin-right: 0.25rem; }
    .ticket-head__subject { font-family: 'Montserrat', sans-serif; font-size: 20px; font-weight: 700; margin: 0.15rem 0 0.6rem; color: var(--bt-text-primary); }
    .ticket-head__meta { display: flex; flex-wrap: wrap; gap: 0.35rem 1.25rem; font-size: 13px; color: var(--bt-text-secondary); }
    .ticket-head__meta a { color: var(--bt-text-link); text-decoration: none; }
    .meta-k { color: var(--bt-text-muted); font-weight: 600; margin-right: 0.3rem; }
    .override-dot { color: var(--bt-status-warning); font-size: 16px; line-height: 0; margin-left: 2px; }

    .ticket-grid { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 1rem; align-items: start; }
    .ticket-grid--safe { grid-template-columns: minmax(0, 1fr); }
    .ticket-main { display: flex; flex-direction: column; gap: 1rem; min-width: 0; }
    .ticket-rail { display: flex; flex-direction: column; gap: 1rem; position: sticky; top: 1rem; }

    .card-head {
      padding: 0.75rem 1rem; font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 700;
      text-transform: uppercase; letter-spacing: 0.05em; color: var(--bt-text-secondary);
      border-bottom: 1px solid var(--bt-border-light);
    }
    .conversation__body { padding: 1rem; max-height: 460px; overflow-y: auto; }
    .conversation__compose { padding: 0.85rem 1rem; border-top: 1px solid var(--bt-border-light); background: var(--bt-bg-surface-2); border-radius: 0 0 12px 12px; }
    .rail-card { padding: 0; }
    .rail-card .card-head { border-bottom: 1px solid var(--bt-border-light); }
    .rail-card > :not(.card-head) { padding: 0.85rem 1rem; }

    .history { list-style: none; margin: 0; padding: 0.5rem 1rem !important; display: flex; flex-direction: column; gap: 0.45rem; }
    .history__row { font-size: 13px; color: var(--bt-text-primary); }
    .history__k { font-weight: 600; color: var(--bt-text-muted); margin-right: 0.35rem; }
    .history__who { color: var(--bt-text-muted); font-size: 12px; margin-left: 0.25rem; }
    .history__empty { color: var(--bt-text-muted); font-size: 13px; }
    .audit-line { margin: 0; font-size: 13px; color: var(--bt-text-secondary); }
    .audit-count { font-weight: 700; color: var(--bt-text-primary); }

    @media (max-width: 900px) { .ticket-grid { grid-template-columns: 1fr; } .ticket-rail { position: static; } }
  `],
})
export class TicketDetailComponent implements OnInit, OnDestroy {
  private api = inject(SupportApiService);
  private route = inject(ActivatedRoute);
  private focus = inject(SidekickFocusService);
  private destroyRef = inject(DestroyRef);

  private live = inject(LiveRefreshService);
  private notifications = inject(SupportNotificationService);

  ticketId = '';
  projection: 'full' | 'safe' | null = null;
  full: TicketFull | null = null;
  safe: TicketSafe | null = null;
  messages: TicketMessageDetail[] = [];
  transitions: TicketTransition[] = [];
  accessLog: TicketAccessLogEntry[] = [];
  showEscalateModal = false;
  assignableAgents: { id: string; email: string }[] = [];
  /** Message ids already rendered, so a background refresh can tell an ARRIVAL from
   *  a re-read of the same thread. */
  private seenMessageIds = new Set<string>();
  /** Event cursor for the change detector. Seeded once at load from
   *  `GET /tickets/{id}/events?since=0` so the poll asks "anything NEW?" rather than
   *  replaying history on every tick. */
  private lastEventSeq = 0;

  ngOnInit(): void {
    this.ticketId = this.route.snapshot.paramMap.get('id') ?? '';
    // takeUntilDestroyed: if the user navigates away before this response lands,
    // the subscription is torn down so the late callback never fires. Without it,
    // a late `getTicket` callback would run this.focus.setFocus(...) AFTER
    // ngOnDestroy's setFocus(null), leaving the global sidekick focus pointing at
    // the ticket the user already left (codex-review MEDIUM, 2026-08-11).
    this.api.getTicket(this.ticketId).pipe(
      takeUntilDestroyed(this.destroyRef),
      // Fail CLOSED on an unreadable id. The route accepts any id, so a stale or
      // shared deep link reaches here and the API fail-closes (404/403); without this
      // the observable simply errored unhandled. Completing instead leaves
      // `projection` null — the template renders neither branch, and armLiveRefresh
      // below is never reached, so no stream and no poll are opened for an id the
      // principal cannot read.
      catchError(() => EMPTY),
    ).subscribe((result) => {
      this.projection = result.projection;
      if (result.projection === 'full') {
        this.full = result.ticket as TicketFull;
        this.api.getTicketTransitions(this.ticketId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((transitions) => (this.transitions = transitions));
        this.api.getTicketAccessLog(this.ticketId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((log) => (this.accessLog = log));
        // AB#1883: fill the assign picker. Gated on the FULL projection because the
        // route is BT-staff only and the safe branch renders no assign control at all —
        // calling it for a VAR would be a guaranteed 403 for a control that is not on
        // screen. Deliberately keyed off the ticket's own queue_id: the roster is
        // per-queue, so it cannot be fetched before the ticket resolves.
        this.loadAssignableAgents((result.ticket as TicketFull).queue_id);
      } else {
        this.safe = result.ticket as TicketSafe;
      }
      // So the sidekick's getPageContext (sidekick-panel.component.ts) can
      // report the on-screen ticket. Cleared on ngOnDestroy below.
      this.focus.setFocus({
        ticketId: this.ticketId,
        reference: result.ticket.reference,
        orgLabel: result.ticket.organization_id,
      });
      this.armLiveRefresh(result.projection);
    });
    this.api.getTicketMessages(this.ticketId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((messages) => this.applyMessages(messages, { announce: false }));

    // Establish the event cursor once, so the poll below can ask "anything NEW?" rather
    // than "give me everything since 0" (which would report events on every tick and
    // defeat the whole point). One non-audited call at load; the full thread has just
    // been fetched above, so the events themselves are discarded here.
    this.api.getTicketEvents(this.ticketId, 0).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(() => EMPTY),
    ).subscribe((page) => this.advanceEventCursor(page));

  }

  /**
   * AB#1866: the thread the agent actually reads refreshes itself. <app-live-panel>
   * has had an SSE subscription since Task 13, but it holds its OWN copy of the
   * conversation in a side card — this main thread was refreshed only by the agent's
   * own compose/escalate actions, so a customer reply never appeared without a manual
   * page reload. Scoped to THIS ticket; the inbox watches the agent-wide scope
   * separately.
   *
   * 🔴 CALLED ONLY FROM THE `getTicket` SUCCESS HANDLER, and that placement is the
   * guard — not a check inside the subscriber. An earlier revision subscribed here in
   * `ngOnInit` and tested `if (this.projection === null) return;` INSIDE the callback,
   * with a comment claiming "no projection, no watch". That comment described a guard
   * the code did not implement: `LiveRefreshService.watch()` calls
   * `SupportStreamService.connect()` on SUBSCRIBE, so the connection was already open
   * by the time anything read `projection`; the test only suppressed the refetch.
   * A deep-link to a ticket outside the agent's grants (the route accepts any id) then
   * got a fail-closed 404 from routers/stream.py and — with the `response.ok` throw
   * added in this same PR — retried on the 2s backoff for the life of the tab.
   * Not arming until the ticket has RESOLVED is what actually closes that: an id the
   * principal cannot read never reaches this method.
   * (codex-review + code-review + security-check, all three independently, PR #1571.)
   *
   * The `stream` flag is the second half. See LiveRefreshService.watch's `opts.stream`:
   * a `safe` projection is a partner-rail (VAR) principal, and SupportStreamService
   * takes its token only from MsalService, so an SSE connection for them 401s and
   * retries forever. Before this PR a VAR page opened NO connection at all — the live
   * panel renders only under `projection === 'full'` — so arming one unconditionally
   * would have INTRODUCED that loop rather than inherited it. VARs keep the interval,
   * which is what refreshes today anyway. (security-check MEDIUM, PR #1571.)
   */
  private armLiveRefresh(projection: 'full' | 'safe'): void {
    this.live.watch(this.ticketId, { stream: projection === 'full' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.detectThreadChange());
  }

  ngOnDestroy(): void {
    this.focus.setFocus(null);
  }

  slaLabel = slaLabel;
  statusClass = statusBadgeClass;
  priorityClass = priorityBadgeClass;
  slaClass = slaBadgeClass;

  fmt(iso: string | null): string {
    return iso ? new Date(iso).toLocaleString() : '—';
  }

  slaTooltip(): string {
    return this.full?.first_response_due_at
      ? `First-response due ${new Date(this.full.first_response_due_at).toLocaleString()}`
      : 'No first-response SLA clock on this ticket';
  }

  /**
   * Resolve who may be assigned this ticket. Failure is SILENT by design: the picker
   * already renders its own "No grantees resolved for this queue yet" hint on an empty
   * list, so a failed roster read degrades to exactly the state the console showed
   * before this route existed — and it must never take down the ticket page, which is
   * still fully usable for reply, escalate, and priority override without it.
   */
  private loadAssignableAgents(queueId: string): void {
    this.api.listQueueGrantees(queueId).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(() => EMPTY),
    ).subscribe((grantees) => {
      this.assignableAgents = grantees.map((g) => ({ id: g.id, email: g.email }));
    });
  }

  reloadThread(): void {
    // The agent's own action (they just posted / escalated) — never an "arrival".
    this.api.getTicketMessages(this.ticketId).subscribe((messages) => this.applyMessages(messages, { announce: false }));
  }

  /**
   * Ask whether anything changed — WITHOUT reading the messages.
   *
   * This is the CJI-audit fix (compliance-scan AU-2/AU-6/AU-12, PR #1571).
   * `GET /tickets/{id}/messages` writes a `support.ticket_access_log` row on every
   * non-requester read (`routers/tickets.py:289`), so polling it every 3s manufactured
   * roughly 1200 machine-generated read-audit rows per open-tab hour — indistinguishable
   * from a deliberate operator read (same actor_oid, actor_kind and projection) and each
   * carrying a BYTE-IDENTICAL `row_hash`, because `core/access_audit.py` canonicalizes
   * over (ticket_id, actor_oid, actor_kind, projection) with no timestamp. That diluted
   * the "who viewed this CJI and when" answer the table exists to give, and flooded the
   * read-audit panel the console itself renders.
   *
   * `GET /tickets/{id}/events` (routers/stream.py::poll_events) answers the same question
   * and writes NO audit row, so the poll asks here and only pays for an audited message
   * read when something actually changed. Frames are pointers and carry no body text, so
   * an event is only ever a signal to refetch through the authorized path.
   *
   * A failure leaves the thread on screen rather than emptying it — a dropped poll is not
   * evidence the conversation is gone.
   */
  private detectThreadChange(): void {
    this.api.getTicketEvents(this.ticketId, this.lastEventSeq).subscribe({
      next: (page) => {
        const changed = !!page.resync || (page.events?.length ?? 0) > 0;
        this.advanceEventCursor(page);
        if (changed) this.refetchThreadAnnouncing();
      },
      error: () => { /* keep what is on screen; the next tick retries */ },
    });
  }

  /** Cursor advance, shared by the load-time baseline and every poll. Monotonic: never
   *  moves backwards, so an out-of-order or stale response cannot rewind it and cause a
   *  replay of events already handled. */
  private advanceEventCursor(page: TicketEventsPage): void {
    const next = page.resync?.currentEventSeq ?? page.currentEventSeq;
    if (typeof next === 'number') this.lastEventSeq = Math.max(this.lastEventSeq, next);
    for (const e of page.events ?? []) {
      this.lastEventSeq = Math.max(this.lastEventSeq, e.eventSeq);
    }
  }

  private refetchThreadAnnouncing(): void {
    this.api.getTicketMessages(this.ticketId).subscribe({
      next: (messages) => this.applyMessages(messages, { announce: true }),
      error: () => { /* keep what is on screen; the next tick retries */ },
    });
  }

  private applyMessages(messages: TicketMessageDetail[], opts: { announce: boolean }): void {
    const arrived = messages.filter((m) => !this.seenMessageIds.has(m.id));
    for (const m of messages) this.seenMessageIds.add(m.id);
    this.messages = messages;
    if (!opts.announce) return;
    // Only the OTHER side of the conversation is worth interrupting an agent for.
    // The console cannot tell one agent's messages from another's — SupportPrincipalService
    // exposes an email, not the principal id these rows carry — so every agent-authored
    // row is treated as "mine". The cost of that is a missed nudge when a colleague
    // replies on a shared ticket; the alternative (buzzing an agent about their own
    // reply landing) is worse and would be noticed on every single reply.
    const fromOtherCount = arrived.filter((m) => m.author_type !== 'agent').length;
    // noteThreadActivity, NOT noteTickets: this thread is already on screen, so it must
    // flash the tab title without inflating the inbox's "N new tickets" badge.
    if (fromOtherCount > 0) this.notifications.noteThreadActivity(fromOtherCount);
  }

  lastMessageSummary(): string {
    return this.messages.length ? this.messages[this.messages.length - 1].body : '';
  }

  /** The PATCH succeeded, so the status is known without an extra audited ticket read;
   *  History is re-read so the agent sees the transition row it just wrote. */
  onResolved(): void {
    if (this.full) this.full = { ...this.full, status: 'resolved' };
    this.api.getTicketTransitions(this.ticketId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((transitions) => (this.transitions = transitions));
  }

  onEscalated(): void {
    this.showEscalateModal = false;
    this.reloadThread();
  }
}
